import { apiPathPrefix } from "../config/coilConfigurator/appConfig";
import axiosConfig from "./coilConfiguratorAxiosConfig";
import type { AxiosResponse } from "axios";
import {
  defaultRecommendationOptions,
  type RecommendationOptionsInput,
  type StandardEquipmentCatalogue,
} from "../configurator";
import type {
  MeasurementRecord,
  SavedCoilConfiguration,
} from "../web-app";
import type { GlossaryEntry } from "../glossary";
import { GLOSSARY } from "../glossary";

const API = apiPathPrefix;

function unwrapApiPayload<T>(response: AxiosResponse): T {
  const body = response.data;
  if (body && typeof body === "object" && "data" in body && "message" in body) {
    return body.data as T;
  }
  return body as T;
}

async function apiGet<T>(path: string): Promise<T> {
  const response = await axiosConfig.get(`${API}${path}`, {
    headers: { Accept: "application/json" },
  });
  return unwrapApiPayload<T>(response);
}

async function apiPost<T>(path: string, payload: unknown): Promise<T> {
  const response = await axiosConfig.post(`${API}${path}`, payload, {
    headers: { "Content-Type": "application/json", Accept: "application/json" },
  });
  return unwrapApiPayload<T>(response);
}

async function apiPut<T>(path: string, payload: unknown): Promise<T> {
  const response = await axiosConfig.put(`${API}${path}`, payload, {
    headers: { "Content-Type": "application/json", Accept: "application/json" },
  });
  return unwrapApiPayload<T>(response);
}

async function apiDelete<T>(path: string): Promise<T> {
  const response = await axiosConfig.delete(`${API}${path}`, {
    headers: { Accept: "application/json" },
  });
  return unwrapApiPayload<T>(response);
}

type ApiCatalogue = StandardEquipmentCatalogue;
type ApiConfiguration = Omit<SavedCoilConfiguration, "createdAt"> & { createdAt: string };
type ApiMeasurement = Omit<MeasurementRecord, "loggedAt"> & { loggedAt: string };

function parseConfiguration(config: ApiConfiguration): SavedCoilConfiguration {
  return { ...config, createdAt: new Date(config.createdAt) };
}

function parseMeasurement(record: ApiMeasurement): MeasurementRecord {
  return { ...record, loggedAt: new Date(record.loggedAt) };
}

function serializeConfiguration(config: SavedCoilConfiguration): ApiConfiguration {
  return { ...config, createdAt: config.createdAt.toISOString() };
}

function serializeMeasurement(record: MeasurementRecord): ApiMeasurement {
  return { ...record, loggedAt: record.loggedAt.toISOString() };
}

export interface LoadedApplicationData {
  catalogue: StandardEquipmentCatalogue;
  assumptions: RecommendationOptionsInput;
  configurations: SavedCoilConfiguration[];
  measurements: MeasurementRecord[];
  glossary: Record<string, GlossaryEntry>;
}

export async function loadGlossary(): Promise<Record<string, GlossaryEntry>> {
  return apiGet<Record<string, GlossaryEntry>>("/glossary");
}

export async function saveGlossaryEntry(
  entryKey: string,
  entry: GlossaryEntry,
): Promise<GlossaryEntry> {
  const saved = await apiPut<{ title: string; definition: string }>(
    `/glossary/${encodeURIComponent(entryKey)}`,
    entry,
  );
  return { title: saved.title, definition: saved.definition };
}

export async function loadApplicationData(): Promise<LoadedApplicationData> {
  const [catalogue, assumptions, configurations, measurements, glossary] = await Promise.all([
    apiGet<ApiCatalogue>("/catalogue"),
    apiGet<RecommendationOptionsInput | null>("/assumptions"),
    apiGet<ApiConfiguration[]>("/configurations"),
    apiGet<ApiMeasurement[]>("/measurements"),
    loadGlossary().catch(() => GLOSSARY),
  ]);

  return {
    catalogue,
    assumptions: assumptions ?? (defaultRecommendationOptions as RecommendationOptionsInput),
    configurations: configurations.map(parseConfiguration),
    measurements: measurements.map(parseMeasurement),
    glossary: Object.keys(glossary).length > 0 ? glossary : GLOSSARY,
  };
}

export async function saveCatalogue(catalogue: StandardEquipmentCatalogue): Promise<void> {
  const saved = await apiGet<ApiCatalogue>("/catalogue").catch(() => ({
    powerSources: [],
    coilFrames: [],
    bottomEnds: [],
  }));

  const savedPowerIds = new Set((saved.powerSources ?? []).map((item) => item.id));
  const activePowerIds = new Set((catalogue.powerSources ?? []).map((item) => item.id));
  const savedFrameIds = new Set((saved.coilFrames ?? []).map((item) => item.id));
  const activeFrameIds = new Set((catalogue.coilFrames ?? []).map((item) => item.id));
  const savedBottomIds = new Set((saved.bottomEnds ?? []).map((item) => item.id));
  const activeBottomIds = new Set((catalogue.bottomEnds ?? []).map((item) => item.id));

  await Promise.all([
    ...(catalogue.powerSources ?? []).map((item) => apiPost("/catalogue/power-sources", item)),
    ...(catalogue.coilFrames ?? []).map((item) => apiPost("/catalogue/coil-frames", item)),
    ...(catalogue.bottomEnds ?? []).map((item) => apiPost("/catalogue/bottom-ends", item)),
    ...[...savedPowerIds].filter((id) => !activePowerIds.has(id)).map((id) => apiDelete(`/catalogue/power-sources/${id}`)),
    ...[...savedFrameIds].filter((id) => !activeFrameIds.has(id)).map((id) => apiDelete(`/catalogue/coil-frames/${id}`)),
    ...[...savedBottomIds].filter((id) => !activeBottomIds.has(id)).map((id) => apiDelete(`/catalogue/bottom-ends/${id}`)),
  ]);
}

export async function saveAssumptions(assumptions: RecommendationOptionsInput): Promise<void> {
  await apiPut("/assumptions", assumptions);
}

export async function saveConfiguration(config: SavedCoilConfiguration): Promise<void> {
  await apiPost("/configurations", serializeConfiguration(config));
}

export async function deleteConfiguration(id: string): Promise<void> {
  await apiDelete(`/configurations/${id}`);
}

export async function saveMeasurement(record: MeasurementRecord): Promise<void> {
  await apiPost("/measurements", serializeMeasurement(record));
}

export async function deleteMeasurement(id: string): Promise<void> {
  await apiDelete(`/measurements/${id}`);
}
